import { useRef, useState, type ReactNode } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Flex,
  Icon,
  Skeleton,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import { EditableTitle } from '@/shared/components/EditableTitle';
import {
  ChatEdgeFade,
  CHAT_EDGE_FADE_CLEARANCE,
} from '@/shared/components/ChatEdgeFade';
import { DISPLAY_NAME_MAX_LENGTH, UI_ACTION_DEBOUNCE_MS } from '@/shared/utils';

import type { IThreadOption } from './ThreadSelector';

/** Gap between the menu trigger and the popover, per the header spec. */
const MENU_SIDE_OFFSET = 6;

/**
 * Top padding a message list needs to clear the header's fade strip — the
 * first message would otherwise start underneath the gradient.
 */
export const CHAT_HEADER_FADE_CLEARANCE_CLASS = CHAT_EDGE_FADE_CLEARANCE.top;

/** The header's back button: where it goes and what it's called. */
export interface IChatThreadHeaderBack {
  /** Accessible name and tooltip, e.g. "Back to all drafts". */
  label: string;
  /** Leaves the chat column. Where "back" goes is the caller's business. */
  onClick: () => void;
}

/** Copy for the header. Owned by the caller. */
export interface IChatThreadHeaderLabels {
  /** Accessible name for the options menu trigger. */
  options: string;
  /** "Rename chat" menu item. */
  rename: string;
  /** Heading above the thread list in the menu. */
  previousChats: string;
  /** "New chat" menu item. */
  newChat: string;
  /** Shown in place of the name when no thread is selected. */
  untitled: string;
  /** Placeholder inside the name field while editing. */
  namePlaceholder: string;
}

interface IChatThreadHeaderProps {
  /** Thread rows, already built + disambiguated by the caller. */
  options: IThreadOption[];
  /** Currently selected thread id (controlled by the caller). */
  selectedThreadId?: string;
  /** Called when the user picks an existing thread from the menu. */
  onSelectThread: (threadId: string) => void;
  /** Fires when the menu opens, e.g. to refetch the latest thread list. */
  onOpen?: () => void;
  /** Called when the user creates a new thread. Debounced internally. */
  onCreateThread: () => void;
  /** Called when the user renames the selected thread. */
  onRenameThread: (displayName: string) => Promise<void>;
  /**
   * The back button. Omit it when the page header already has one, so
   * there's only one.
   */
  back?: IChatThreadHeaderBack;
  /** Additional actions shown beside the thread controls. */
  actions?: ReactNode;
  /** Which side the thread cluster sits on; the actions take the other end. Defaults to `end`. */
  align?: 'start' | 'end';
  /** Disables the name field and the menu actions while creating. */
  isCreating?: boolean;
  /**
   * The thread list is still being fetched. The name is a skeleton and editing
   * is off until it arrives: `options` is empty until then, so the name would
   * otherwise read as untitled for a thread that already has a title, and a
   * rename started in that window gets clobbered by the value sync.
   */
  isLoading?: boolean;
  /** All user-facing copy. */
  labels: IChatThreadHeaderLabels;
  /** Distinguishes header instances in tests. */
  dataTestId: string;
}

/**
 * Header row at the top of a chat column: an optional hard-left back button and a
 * thread cluster (glyph · editable chat name · options menu), right-aligned by
 * default or left with `align="start"`, the actions at the far end.
 *
 * 56px tall and unfilled, so it shares a centerline with the document title
 * bar and the rail's action row and lets the `--layout` page surface through.
 * Purely presentational: the caller owns the thread data and mutations.
 */
export function ChatThreadHeader({
  options,
  selectedThreadId,
  onSelectThread,
  onOpen,
  onCreateThread,
  onRenameThread,
  back,
  actions,
  align = 'end',
  isCreating = false,
  isLoading = false,
  labels,
  dataTestId,
}: IChatThreadHeaderProps) {
  // Bumped by "Rename chat" to remount the title in edit mode — `EditableTitle`
  // only honours `autoFocusEdit` on mount, so the key is the way to re-arm it.
  const [renameNonce, setRenameNonce] = useState(0);
  // Set by "Rename chat" and consumed once the menu has closed. Arming the
  // field during `onSelect` doesn't work: the field mounts while the menu is
  // still closing, and the close moves focus away — which blurs the input, and
  // a blur commits, so the field shuts the instant it opened.
  const isArmingRenameRef = useRef(false);

  const selectedLabel = options.find(
    option => option.value === selectedThreadId,
  )?.label;

  const debouncedCreateThread = useDebounceCallback(
    onCreateThread,
    UI_ACTION_DEBOUNCE_MS,
    { leading: true, trailing: false },
  );

  return (
    <>
      <Flex
        align="center"
        width="full"
        className="h-14 shrink-0"
        data-testid={dataTestId}
      >
        {back && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={back.onClick}
                aria-label={back.label}
                // The header row is unpadded, so the button carries its own
                // inset. `ml-1` lands the 16px glyph on the 12px gutter the
                // message list uses (px-3), and — unlike a negative margin —
                // keeps the 32px hover pill inside the column, which is clipped
                // by the chat's `overflow-hidden`.
                className="ml-1 size-8 shrink-0 rounded-lg text-foreground hover:bg-accent"
              >
                <Icon type="ArrowLeft" className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{back.label}</TooltipContent>
          </Tooltip>
        )}
        <Flex
          align="center"
          gap={0.5}
          className={cn(
            'min-w-0 max-w-[75%]',
            // 20px in, the same as the composer box and the first line of each message.
            align === 'start' ? 'ml-5 justify-start' : 'ml-auto justify-end',
          )}
        >
          <Icon
            type="MessageCircle"
            aria-hidden
            className="size-3 shrink-0 text-muted-foreground"
          />
          {isLoading ? (
            <Skeleton
              className="h-5 w-32 rounded-sm"
              data-testid="chat-thread-name-loading"
            />
          ) : (
            <EditableTitle
              key={`chat-name-${renameNonce}`}
              value={selectedLabel ?? labels.untitled}
              onRename={onRenameThread}
              isEditable={Boolean(selectedThreadId)}
              isDisabled={isCreating}
              autoFocusEdit={renameNonce > 0}
              // 12/16 per the header spec. `xs` sizes the display text, the
              // hidden twin and the input together — passing `text-xs` via
              // className would size only the first two and the field would
              // jump on entering edit mode.
              level="xs"
              weight="normal"
              className="min-w-0 max-w-full text-muted-foreground hover:text-foreground"
              maxLength={DISPLAY_NAME_MAX_LENGTH}
              emptyFallback={labels.untitled}
              placeholder={labels.namePlaceholder}
              textTestId="chat-thread-name"
            />
          )}
          <DropdownMenu
            onOpenChange={open => {
              if (open) onOpen?.();
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={labels.options}
                className="size-[22px] shrink-0 rounded text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted"
              >
                <Icon type="ChevronDown" className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={MENU_SIDE_OFFSET}
              className="w-[260px]"
              onCloseAutoFocus={event => {
                if (!isArmingRenameRef.current) return;
                isArmingRenameRef.current = false;
                // Keep focus off the trigger; the name input takes it instead.
                event.preventDefault();
                setRenameNonce(nonce => nonce + 1);
              }}
            >
              <DropdownMenuItem
                disabled={!selectedThreadId || isCreating || isLoading}
                onSelect={() => {
                  isArmingRenameRef.current = true;
                }}
              >
                {/* Lattice's icon set has no bare `Pencil`. */}
                <Icon type="PencilLine" className="size-3.5" />
                {labels.rename}
              </DropdownMenuItem>
              {options.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="px-2 pt-1 pb-0.5 text-xs leading-4 font-normal text-muted-foreground">
                    {labels.previousChats}
                  </DropdownMenuLabel>
                  {options.map(option => {
                    const isActive = option.value === selectedThreadId;
                    return (
                      <DropdownMenuItem
                        key={option.value}
                        onSelect={() => onSelectThread(option.value)}
                        className={isActive ? 'bg-accent' : undefined}
                      >
                        <Text
                          level="small"
                          className="min-w-0 flex-1 truncate"
                          data-testid="chat-thread-option-label"
                        >
                          {option.label}
                        </Text>
                        {option.description && (
                          <Text
                            level="xSmall"
                            color="muted"
                            className="shrink-0 whitespace-nowrap"
                          >
                            {option.description}
                          </Text>
                        )}
                        {isActive && (
                          <Icon
                            type="Check"
                            className="size-3.5 shrink-0"
                            data-testid="chat-thread-option-active"
                          />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={isCreating}
                onSelect={debouncedCreateThread}
              >
                <Icon type="Plus" className="size-3.5" />
                {labels.newChat}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Flex>
        {actions && (
          // Like the back button's `ml-1`: the row is unpadded and clips the hover pill.
          <Flex
            align="center"
            gap={1}
            className={cn('mr-1 shrink-0', align === 'start' && 'ml-auto')}
          >
            {actions}
          </Flex>
        )}
      </Flex>
      <ChatEdgeFade edge="top" />
    </>
  );
}
